/**
 * SupportPage — customer-facing Help & Support hub.
 *
 * Replaces the previous behaviour where tapping "Help & Support" in the
 * profile menu deep-linked straight to WhatsApp with a prefilled
 * message. Now shows a proper page with FAQs, quick contact options
 * (WhatsApp / Call as configured by the tenant), and entry points to
 * Orders / Bookings / T&C / Privacy.
 *
 * Tenant's support_contact (kind, numbers, message) is already on the
 * resolved tenant document available via useTenant() — no extra API
 * call needed.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    MessageCircle, Phone, ChevronRight, ChevronDown, Mail,
    ShoppingBag, Calendar, FileText, ShieldCheck, Headphones, HelpCircle,
} from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { BkPageHeader } from "../../components/blinkit/BkUI";
import { LseSupportSection, useTenantFaqs } from "../../components/lse/LseSupport";

const FAQS = [
    {
        q: "How do I track my order?",
        a: "Open My Orders from your profile, tap an order to see live status, rider details (after assignment) and ETA.",
    },
    {
        q: "How do I cancel an order?",
        a: "You can cancel from the order detail page as long as the store hasn't accepted it. For accepted/confirmed orders, please contact us via the buttons below.",
    },
    {
        q: "Where is my refund?",
        a: "Refunds reflect in your Wallet immediately and in your original bank account within 5–7 working days, depending on your bank.",
    },
    {
        q: "How do I redeem coins?",
        a: "Add items to cart, head to checkout and choose 'Apply Coins'. Subject to the per-order cap configured by your store.",
    },
    {
        q: "I never received an OTP — what now?",
        a: "Try the WhatsApp OTP option on the login screen, or contact us via the buttons below with your mobile number.",
    },
];

function FaqItem({ q, a, idx }) {
    const [open, setOpen] = useState(false);
    return (
        <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`support-faq-${idx}`}
            className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-muted/40"
        >
            <HelpCircle size={18} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{q}</p>
                {open && <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{a}</p>}
            </div>
            <ChevronDown size={16}
                className={`text-muted-foreground mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
    );
}

export default function SupportPage() {
    // Blinkit-theme tenants get the shared Blinkit header; the page body is the same for every theme.
    const blinkit = useIsBlinkit();
    const { tenant } = useTenant();
    // Location Service Engine: the tenant's own FAQ replaces the built-in one when it has any (null otherwise).
    const tenantFaqs = useTenantFaqs();
    const faqList = tenantFaqs ? tenantFaqs.map((f) => ({ q: f.question, a: f.answer })) : FAQS;

    // Build the WhatsApp + Call deep-links the same way the order-track
    // page does (see /api/customer/orders).
    const links = useMemo(() => {
        const sc = tenant?.support_contact || {};
        const kind = (sc.kind || "WHATSAPP").toUpperCase();
        const message = sc.message || "Hi, I need help.";
        const fallback = sc.number || tenant?.contact_mobile || "";
        const waRaw = sc.whatsapp_number || fallback;
        const callRaw = sc.call_number || fallback;
        const wa = (kind === "WHATSAPP" || kind === "BOTH") && waRaw ? {
            number: waRaw,
            url: `https://wa.me/${waRaw.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`,
        } : null;
        const call = (kind === "CALL" || kind === "BOTH") && callRaw ? {
            number: callRaw,
            url: `tel:${callRaw}`,
        } : null;
        return { wa, call, email: tenant?.contact_email || sc.email || "" };
    }, [tenant]);

    return (
        <div className="pb-32" data-testid="support-page">
            {blinkit ? (
                <BkPageHeader title="Help & support" subtitle="Pick the fastest way to reach us" back="/profile" />
            ) : (
                <header className="px-4 pt-5 pb-4 bg-card border-b border-border">
                    <h1 className="text-2xl font-display font-bold inline-flex items-center gap-2">
                        <Headphones size={22} className="text-primary" /> Help &amp; Support
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        We&apos;re here for you — pick the fastest channel.
                    </p>
                </header>
            )}

            {/* iter178 — desktop (lg+): two columns, same convention as Cart / Checkout. Below lg both wrappers are plain
                blocks, so phones and tablets render exactly as before. */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:items-start">
            <div className="min-w-0">
            {/* Quick contact buttons */}
            <section className="mx-4 mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3" data-testid="support-contact-grid">
                {links.wa && (
                    <a
                        href={links.wa.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="support-whatsapp"
                        className="bg-emerald-500 text-white rounded-2xl p-4 flex items-center gap-3 active:scale-[0.98] transition shadow-sm"
                    >
                        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
                            <MessageCircle size={22} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">Chat on WhatsApp</p>
                            <p className="font-bold text-sm truncate">{links.wa.number}</p>
                        </div>
                        <ChevronRight size={18} />
                    </a>
                )}
                {links.call && (
                    <a
                        href={links.call.url}
                        data-testid="support-call"
                        className="bg-primary text-primary-foreground rounded-2xl p-4 flex items-center gap-3 active:scale-[0.98] transition shadow-sm"
                    >
                        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
                            <Phone size={22} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">Call us</p>
                            <p className="font-bold text-sm truncate">{links.call.number}</p>
                        </div>
                        <ChevronRight size={18} />
                    </a>
                )}
                {!links.wa && !links.call && (
                    <div className="col-span-full bg-muted/40 border border-dashed border-border rounded-2xl p-4 text-center text-sm text-muted-foreground" data-testid="support-no-contact">
                        Your store hasn&apos;t set up a contact number yet. Please use the FAQ or email instead.
                    </div>
                )}
                {links.email && (
                    <a
                        href={`mailto:${links.email}`}
                        data-testid="support-email"
                        className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3 active:scale-[0.98] transition"
                    >
                        <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center">
                            <Mail size={20} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Email us</p>
                            <p className="font-bold text-sm truncate">{links.email}</p>
                        </div>
                        <ChevronRight size={18} className="text-muted-foreground" />
                    </a>
                )}
            </section>

            {/* Location Service Engine tenants with support tickets only (renders nothing otherwise). */}
            <LseSupportSection />

            {/* Quick links */}
            <section className="mx-4 mt-5">
                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2 px-1">Quick links</p>
                <nav className="bg-card rounded-2xl border border-border divide-y divide-border">
                    {[
                        { Icon: ShoppingBag, label: "My Orders", to: "/orders", testid: "support-link-orders" },
                        { Icon: Calendar, label: "My Bookings", to: "/orders?tab=bookings", testid: "support-link-bookings" },
                        { Icon: FileText, label: "Terms & Conditions", to: "/legal/terms", testid: "support-link-terms" },
                        { Icon: ShieldCheck, label: "Privacy Policy", to: "/legal/privacy", testid: "support-link-privacy" },
                    ].map(({ Icon, label, to, testid }) => (
                        <Link key={label} to={to} data-testid={testid}
                              className="flex items-center gap-4 px-4 py-3.5">
                            <Icon size={18} className="text-muted-foreground" />
                            <span className="flex-1 font-semibold text-sm">{label}</span>
                            <ChevronRight size={16} className="text-muted-foreground" />
                        </Link>
                    ))}
                </nav>
            </section>

            </div>
            <div className="min-w-0 lg:[&>*:first-child]:mt-4">
            {/* FAQs */}
            <section className="mx-4 mt-6">
                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2 px-1">Frequently asked</p>
                <div className="bg-card rounded-2xl border border-border divide-y divide-border overflow-hidden" data-testid="support-faqs">
                    {faqList.map((f, i) => <FaqItem key={i} idx={i} {...f} />)}
                </div>
            </section>
            </div>
            </div>

            <p className="text-[11px] text-muted-foreground text-center mt-8 px-4">
                {tenant?.name || "Bizil"} support · We typically respond within a few hours.
            </p>
        </div>
    );
}
